import { useState } from "react";

import { Button, Card, PageSection, StatusMessage } from "@ceedero/ui";

import { useUpdateDisplayName, type AccountProfile } from "@/account/hooks/use-account";
import { resolveCredentialChangeErrorKey } from "@/account/utils/account-refusal";
import { validateDisplayName } from "@/account/utils/credential-validation";
import { FormField } from "@/components/form-field";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

/**
 * The name shown beside the username, which is optional and is the only field
 * on this page the user writes freely.
 *
 * Emptying it sends `null` rather than `""`: "no display name" is a state the
 * API models, and an empty string is a name made of nothing that would then be
 * drawn as one.
 */
export const DisplayNameSection = ({ profile }: { profile: AccountProfile }) => {
  const { t } = useTranslation();
  const updateDisplayName = useUpdateDisplayName();
  const [displayName, setDisplayName] = useState(profile.displayName ?? "");
  const [fieldError, setFieldError] = useState<TranslationKey | undefined>(undefined);

  const trimmed = displayName.trim();
  const isUnchanged = trimmed === (profile.displayName ?? "");
  const refusalKey = resolveCredentialChangeErrorKey(updateDisplayName.error);

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const error = validateDisplayName(trimmed);
    setFieldError(error);
    if (error !== undefined) {
      return;
    }
    updateDisplayName.mutate(trimmed.length === 0 ? null : trimmed);
  };

  return (
    <PageSection
      title={t("account.displayName.title")}
      description={t("account.displayName.description")}
    >
      <Card className="p-5">
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <FormField
            label={t("account.displayName.label")}
            isLabelHidden
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
              setFieldError(undefined);
              updateDisplayName.reset();
            }}
            placeholder={t("account.displayName.placeholder")}
            autoComplete="nickname"
            maxLength={200}
            errorMessage={fieldError === undefined ? undefined : t(fieldError)}
          />

          {refusalKey !== undefined && <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>}
          {updateDisplayName.isSuccess && (
            <StatusMessage tone="success">{t("account.displayName.saved")}</StatusMessage>
          )}

          <div className="flex justify-end">
            <Button type="submit" isLoading={updateDisplayName.isPending} disabled={isUnchanged}>
              {t("account.save")}
            </Button>
          </div>
        </form>
      </Card>
    </PageSection>
  );
};
